import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useProducts } from '../../hooks/useProducts';
import styles from './CommandPalette.module.css';

interface Cmd {
  id: string;
  label: string;
  desc?: string;
  icon: string;
  path?: string;
  action?: () => void;
  type: 'nav' | 'product' | 'action';
  keywords?: string[];
}

const NAV: Omit<Cmd, 'type'>[] = [
  { id: 'explore',     icon: '◈', label: 'browse marketplace',     desc: 'Explore all AI agents',      path: '/explore',                   keywords: ['browse','search','explore'] },
  { id: 'pricing',    icon: '◇', label: 'view pricing',            desc: 'Plans & pricing',            path: '/pricing',                    keywords: ['pricing','plans','cost'] },
  { id: 'sellers',    icon: '→', label: 'seller guide',            desc: 'How to sell on Aiphra',       path: '/sellers',                    keywords: ['sell','guide','start'] },
  { id: 'support',    icon: '?', label: 'support & docs',          desc: 'Help center',                path: '/support',                    keywords: ['help','support','docs','faq'] },
  { id: 'dashboard',  icon: '⊞', label: 'open dashboard',          desc: 'Seller dashboard',           path: '/dashboard',                  keywords: ['dashboard','seller'] },
  { id: 'products',   icon: '◈', label: 'my products',             desc: 'Your listings',              path: '/dashboard/products',         keywords: ['my products','listings'] },
  { id: 'new-product',icon: '+', label: 'new product',             desc: 'Create a new listing',       path: '/dashboard/products/new',     keywords: ['new','create','add','list'] },
  { id: 'listings',   icon: '◧', label: 'listings & bundles',      desc: 'Manage bundles',             path: '/dashboard/listings',         keywords: ['bundles','listings'] },
  { id: 'wallet',     icon: '◎', label: 'wallet & earnings',       desc: 'Balance & transactions',     path: '/dashboard/wallet',           keywords: ['wallet','money','earnings','balance'] },
  { id: 'analytics',  icon: '◱', label: 'analytics',              desc: 'Stats & performance',        path: '/dashboard/analytics',        keywords: ['analytics','stats'] },
  { id: 'promote',    icon: '⭐', label: 'promote products',        desc: 'Advertising packages',       path: '/dashboard/promote',          keywords: ['promote','ads','featured'] },
  { id: 'launch-kit', icon: '🚀',label: 'launch kit',              desc: 'PR materials for launch',    path: '/dashboard/launch-kit',       keywords: ['launch','pr','marketing'] },
  { id: 'orders',     icon: '↓', label: 'my orders',               desc: 'Purchase history',           path: '/account/purchases',          keywords: ['orders','purchases','history'] },
  { id: 'starred',    icon: '★', label: 'starred products',        desc: 'Saved items',                path: '/account/wishlist',           keywords: ['starred','wishlist','saved'] },
  { id: 'following',  icon: '◎', label: 'following',               desc: 'Sellers you follow',         path: '/account/following',          keywords: ['following','sellers'] },
  { id: 'settings',   icon: '⚙', label: 'settings',               desc: 'Account settings',           path: '/dashboard/settings',         keywords: ['settings','account','profile'] },
];

function fuzzy(query: string, cmd: Cmd): boolean {
  const q = query.toLowerCase();
  return (
    cmd.label.toLowerCase().includes(q) ||
    (cmd.desc?.toLowerCase().includes(q) ?? false) ||
    (cmd.keywords?.some(k => k.includes(q)) ?? false)
  );
}

interface CommandPaletteProps { onClose: () => void }

export default function CommandPalette({ onClose }: CommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  // Reset the selection when the search results change. Adjusted during
  // render (not in an effect) per https://react.dev/learn/you-might-not-need-an-effect.
  const [prevQuery, setPrevQuery] = useState(query);
  if (query !== prevQuery) {
    setPrevQuery(query);
    setActiveIdx(0);
  }
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef  = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { data: products = [] } = useProducts({
    search: query.length >= 2 ? query : undefined,
    limit: 5,
  });

  const filtered = useMemo(() => {
    const navCmds: Cmd[] = NAV.map(n => ({ ...n, type: 'nav' as const }));
    return query ? navCmds.filter(c => fuzzy(query, c)) : navCmds.slice(0, 8);
  }, [query]);

  const productCmds: Cmd[] = useMemo(() => products.slice(0, 4).map(p => ({
    id:    `p-${p.id}`,
    icon:  '📦',
    label: p.slug,
    desc:  `${p.title} — ${ p.is_free ? 'free' : `$${p.price}` } · ${p.sales_count} installs`,
    type:  'product' as const,
    path:  `/product/${p.slug}`,
  })), [products]);

  const all = useMemo(
    () => [...filtered, ...(query.length >= 2 ? productCmds : [])],
    [filtered, productCmds, query]
  );

  const execute = useCallback((cmd: Cmd) => {
    if (cmd.path)   { navigate(cmd.path); onClose(); return; }
    if (cmd.action) { cmd.action(); onClose(); return; }
  }, [navigate, onClose]);

  // Keyboard handler
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx(i => Math.min(i + 1, all.length - 1)); }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setActiveIdx(i => Math.max(i - 1, 0)); }
      if (e.key === 'Enter')     { if (all[activeIdx]) execute(all[activeIdx]); }
      if (e.key === 'Escape')    { onClose(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [all, activeIdx, execute, onClose]);

  // Scroll active into view
  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-idx="${activeIdx}"]`) as HTMLElement | null;
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIdx]);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const groups: { label?: string; items: Cmd[] }[] = [];
  const navResults = all.filter(c => c.type === 'nav');
  const prodResults = all.filter(c => c.type === 'product');
  if (navResults.length)  groups.push({ label: query ? 'Commands' : 'Quick navigation', items: navResults });
  if (prodResults.length) groups.push({ label: '$ search results', items: prodResults });

  let globalIdx = 0;

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.palette} onClick={e => e.stopPropagation()}>
        {/* Input */}
        <div className={styles.inputRow}>
          <span className={styles.prompt}>⌘</span>
          <input
            ref={inputRef}
            className={styles.input}
            placeholder="type a command or search products..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            spellCheck={false}
          />
          <kbd className={styles.escHint}>esc</kbd>
        </div>

        {/* Results */}
        <div className={styles.results} ref={listRef}>
          {groups.map(group => (
            <div key={group.label} className={styles.group}>
              {group.label && (
                <div className={styles.groupLabel}>{group.label}</div>
              )}
              {group.items.map(cmd => {
                const idx = globalIdx++;
                return (
                  <button
                    key={cmd.id}
                    data-idx={idx}
                    className={[styles.item, activeIdx === idx ? styles.itemActive : ''].join(' ')}
                    onMouseEnter={() => setActiveIdx(idx)}
                    onClick={() => execute(cmd)}
                  >
                    <span className={styles.itemIcon}>{cmd.icon}</span>
                    <div className={styles.itemText}>
                      <span className={styles.itemLabel}>
                        {cmd.type === 'product' ? '$ install ' : ''}{cmd.label}
                      </span>
                      {cmd.desc && <span className={styles.itemDesc}>{cmd.desc}</span>}
                    </div>
                    {activeIdx === idx && (
                      <kbd className={styles.enterHint}>↵</kbd>
                    )}
                  </button>
                );
              })}
            </div>
          ))}

          {all.length === 0 && query && (
            <div className={styles.empty}>
              <span className={styles.emptyPrompt}>$</span>
              <span> no results for </span>
              <code className={styles.emptyQuery}>"{query}"</code>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <span className={styles.footerHint}><kbd>↑↓</kbd> navigate</span>
          <span className={styles.footerHint}><kbd>↵</kbd> select</span>
          <span className={styles.footerHint}><kbd>esc</kbd> close</span>
          <span className={styles.footerHint}><kbd>⌘K</kbd> toggle</span>
        </div>
      </div>
    </div>
  );
}
